import { useQuery } from '@tanstack/react-query'
import { CreatorType, PracticeTestStatus } from '@/types/practice-test'
import { axiosInstance } from '@/lib/axios'

export interface HeadingResItem {
  id: number
  class_id: number
  name: string
  position: number
}

export const getHeadings = async (
  classId: string,
  page: number,
  limit: number,
  isPractice: boolean
) => {
  const response = await axiosInstance.get(`/class/${classId}/headings`, {
    params: {
      page,
      limit,
      is_practice: isPractice,
    },
  })
  return response.data
}

export const getTestByHeading = async ({
  headingId,
  page = 1,
  limit = 100,
  status,
  search,
  creator_type,
  isPractice,
}: {
  headingId: string
  page?: number
  limit?: number
  status?: PracticeTestStatus | null
  search: string
  creator_type: CreatorType | null
  isPractice: boolean
}) => {
  const response = await axiosInstance.get(
    `/class/headings/${headingId}/admin-practice-tests`,
    {
      params: {
        page,
        limit,
        ...(status != null ? { status } : {}),
        search,
        creator_type: creator_type ?? undefined,
        is_practice: isPractice,
      },
    }
  )
  return response.data
}

export const useGetHeadings = ({
  classId,
  enabled,
  page = 1,
  limit = 10,
  isPractice,
}: {
  classId: string
  enabled: boolean
  page?: number
  limit?: number
  isPractice: boolean
}) => {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['headings', classId, enabled, page, limit, isPractice],
    queryFn: () => getHeadings(classId, page, limit, isPractice),
    enabled: !!classId && enabled,
  })

  return {
    headings: data?.data,
    isLoadingHeadings: isLoading,
    errorHeadings: error,
    meta: data?.meta,
    refetchHeadings: refetch,
  }
}

export const useGetTestByHeading = ({
  headingId,
  page = 1,
  limit = 100,
  status,
  search,
  creator_type,
  isPractice,
}: {
  headingId: string
  page?: number
  limit?: number
  status?: PracticeTestStatus | null
  search: string
  creator_type: CreatorType | null
  isPractice: boolean
}) => {
  const { data, isLoading, error } = useQuery({
    queryKey: [
      'testByHeading',
      headingId,
      page,
      limit,
      status ?? 'all',
      search,
      creator_type,
      isPractice,
    ],
    queryFn: () =>
      getTestByHeading({
        headingId,
        page,
        limit,
        status,
        search,
        creator_type,
        isPractice,
      }),
    enabled: !!headingId,
  })

  return {
    groupedTests: data?.data,
    isLoadingGroupedTests: isLoading,
    errorGroupedTests: error,
  }
}
